import { Alert, Badge, Box, Flex, Select, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconShieldCheck } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SecurityLogEntry, SecurityLogQuery, SecurityLogResponse } from '@/api';
import { usePagination } from '../../hooks/usePagination';

function formatRelative(ts: string, now: Date): string {
  const then = new Date(ts).getTime();
  const deltaSec = Math.floor((now.getTime() - then) / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.floor(deltaHr / 24);
  if (deltaDay < 30) return `${deltaDay}d ago`;
  return new Date(ts).toLocaleDateString();
}

function formatAbsolute(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export interface SecurityLogProps {
  /**
   * Service-specific fetcher that returns a paginated security log response.
   * Each service's API client exposes `getSecurityLog` — just pass it in bound
   * (e.g. `api.getSecurityLog.bind(api)` or `(q) => api.getSecurityLog(q)`).
   */
  getSecurityLog: (query: SecurityLogQuery) => Promise<SecurityLogResponse>;
  /**
   * Optional localStorage key used to persist the per-page selection. Apps
   * should namespace this (e.g. `formshive.securityLog.pagination`) to avoid
   * cross-app collisions when the same browser serves multiple services.
   */
  storageKey?: string;
  /**
   * Initial page size; overridden by localStorage when present.
   */
  defaultPerPage?: number;
}

export function SecurityLog({ getSecurityLog, storageKey, defaultPerPage = 50 }: SecurityLogProps) {
  const { t } = useTranslation();

  const [perPage, setPerPage] = useState(() => {
    if (storageKey && typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : defaultPerPage;
    }
    return defaultPerPage;
  });
  const perPageRef = useRef(perPage);

  // Refreshes the "x ago" labels without re-fetching.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const fetchSecurityLog = async (params: { nextPage: number; [key: string]: any }) => {
    const { nextPage } = params;
    const currentPerPage = perPageRef.current;
    const res = await getSecurityLog({
      page: nextPage - 1,
      per_page: currentPerPage,
    });
    return {
      data: res.data,
      total: res.total,
    };
  };

  const pagination = usePagination<SecurityLogEntry>({
    perPage,
    fetchData: fetchSecurityLog,
  });

  const handlePerPageChange = (value: string | null) => {
    const newPerPage = parseInt(value || `${defaultPerPage}`, 10);
    setPerPage(newPerPage);
    perPageRef.current = newPerPage;
    if (storageKey && typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(storageKey, newPerPage.toString());
    }
    pagination.loadPage(1);
  };

  const paginationOptions = [
    { value: '10', label: '10' },
    { value: '20', label: '20' },
    { value: '30', label: '30' },
    { value: '40', label: '40' },
    { value: '50', label: '50' },
  ];

  const columns = [
    {
      accessor: 'timestamp',
      title: t('securityLog.when'),
      render: (row: SecurityLogEntry) => (
        <Tooltip label={formatAbsolute(row.timestamp)} withArrow>
          <Text size="sm">{formatRelative(row.timestamp, now)}</Text>
        </Tooltip>
      ),
    },
    {
      accessor: 'operation_label',
      title: t('securityLog.event'),
      render: (row: SecurityLogEntry) => <Text size="sm">{row.operation_label}</Text>,
    },
    {
      accessor: 'method',
      title: t('securityLog.method'),
      render: (row: SecurityLogEntry) =>
        row.method ? (
          <Badge color="gray" variant="light" radius="sm">
            {row.method}
          </Badge>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
    },
    {
      accessor: 'location',
      title: t('securityLog.location'),
      render: (row: SecurityLogEntry) => (
        <Text size="sm" c="dimmed">
          {row.location}
        </Text>
      ),
    },
    {
      accessor: 'device',
      title: t('securityLog.device'),
      render: (row: SecurityLogEntry) => (
        <Text size="sm" c="dimmed">
          {row.device}
        </Text>
      ),
    },
    {
      accessor: 'success',
      title: t('securityLog.status'),
      render: (row: SecurityLogEntry) =>
        row.success ? (
          <Badge color="green" variant="light" radius="sm">
            {t('securityLog.ok')}
          </Badge>
        ) : (
          <Badge
            color="red"
            variant="light"
            radius="sm"
            leftSection={<IconAlertTriangle size={12} />}
          >
            {t('securityLog.failed')}
          </Badge>
        ),
    },
  ];

  return (
    <Stack gap="md">
      <Flex justify="flex-end">
        <Select
          data={paginationOptions}
          value={perPage.toString()}
          onChange={handlePerPageChange}
          size="sm"
          w={80}
        />
      </Flex>
      {pagination.error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title={t('securityLog.errorTitle')}>
          {pagination.error}
        </Alert>
      )}
      <DataTable
        minHeight={(pagination.data?.length ?? 0) === 0 ? 200 : 0}
        withTableBorder
        borderRadius="md"
        shadow="xs"
        records={pagination.data || []}
        columns={columns}
        totalRecords={pagination.total}
        recordsPerPage={pagination.perPage}
        page={pagination.page}
        onPageChange={pagination.setPage}
        fetching={pagination.loading}
        noRecordsText=""
        emptyState={
          (pagination.data?.length ?? 0) === 0 ? (
            <Box py="xl" px="md">
              <Stack align="center" gap="xs">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                  <IconShieldCheck size={24} />
                </ThemeIcon>
                <Text size="sm" fw={500}>
                  {t('securityLog.empty')}
                </Text>
                <Text size="sm" c="dimmed" ta="center" maw={320}>
                  {t(
                    'securityLog.emptyDescription',
                    'Sign-ins, account changes, and other security-relevant events will appear here.'
                  )}
                </Text>
              </Stack>
            </Box>
          ) : (
            <></>
          )
        }
      />
    </Stack>
  );
}
